import { notification } from "antd"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { Workarea } from "../job"
import { WorkareaGroup, WorkareaGroupWithWorkareas } from "./group"
import { WorkareaAttachmentDetail, createWorkareaGroup, deleteWorkareaGroup, getWorkareaAttachments, getWorkareaGroups, moveWorkarea, MoveWorkareaRequest, moveWorkareaToHiddenGroup, MoveWorkareaToHiddenGroupRequest, partiallyEditWorkareaGroup, PartialWorkareaGroupUpdate, setGroupAttachments, setWorkareaAttachments, sortWorkareaGroups, sortWorkareas } from "./groupsApi"
import { useJobItemsInvalidator } from "../../components/Items/itemQueries"

export type WorkareaGroupMutationData = {
  isValidUpdate: boolean
  optimisticUpdate: Partial<WorkareaGroup>
  dataForServer: PartialWorkareaGroupUpdate
}

export const useWorkareaGroupsQuery = (jobId: number) => {
  return useQuery(['workareaGroups', { jobId }], {
    queryFn: () => getWorkareaGroups(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false,
  })
}

export const useWorkareaGroupsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateForJob: (jobId: number) => queryClient.invalidateQueries(['workareaGroups', { jobId }])
  }
}

export const useWorkareaGroupsOptimisticUpdate = () => {
  const queryClient = useQueryClient()
  return {
    setFullOptimisticUpdate: (jobId: number, optimisticUpdate: WorkareaGroupWithWorkareas[]) => {
      queryClient.setQueryData(['workareaGroups', { jobId }], optimisticUpdate)
    },
    setGroupOptimisticUpdate: (jobId: number, workareaGroup: Partial<WorkareaGroup>) => {
      const groups = queryClient.getQueryData(['workareaGroups', { jobId }]) as WorkareaGroupWithWorkareas[] | undefined
      const optimisticUpdate = groups?.map(g => g.id === workareaGroup.id ? { ...g, ...workareaGroup } : g)
      queryClient.setQueryData(['workareaGroups', { jobId }], optimisticUpdate)
    },
    setWorkareaOptimisticUpdate: (jobId: number, workareaUpdate: Partial<Workarea>) => {
      const groups = queryClient.getQueryData(['workareaGroups', { jobId }]) as WorkareaGroupWithWorkareas[] | undefined
      if (groups) {
        const updatedGroups = groups.map(group => ({
          ...group,
          workareas: group.workareas.map(workarea => workarea.id === workareaUpdate.id ? { ...workarea, ...workareaUpdate } : workarea)
        }))
        queryClient.setQueryData(['workareaGroups', { jobId }], updatedGroups)
      }
    }
  }
}

export const useDeleteWorkareaGroupMutation = (onSuccess: () => void) => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: ({ jobId, workareaGroupId }: { jobId: number, workareaGroupId: string }) => deleteWorkareaGroup(jobId, workareaGroupId),
    onSuccess: (_, parameters) => {
      workareaGroupsInvalidator.invalidateForJob(parameters.jobId)
      onSuccess()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'An error occurred while deleting the workarea group'
      notification.error({ message: errorMessage })
    }
  })
}


export const useSortWorkareaGroupsMutation = () => {
  const workareaGroupsOptimisticUpdate = useWorkareaGroupsOptimisticUpdate()

  type MutationProps = {
    jobId: number
    groupIds: string[]
    optimisticUpdate: WorkareaGroupWithWorkareas[]
  }

  return useMutation({
    mutationFn: ({ jobId, groupIds }: MutationProps) => sortWorkareaGroups(jobId, groupIds),
    onMutate: ({ jobId, optimisticUpdate }) => workareaGroupsOptimisticUpdate.setFullOptimisticUpdate(jobId, optimisticUpdate),
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'An error occurred while sorting the workarea groups'
      notification.error({ message: errorMessage })
    }
  })
}

export const useSortWorkareasMutation = () => {
  const workareaGroupsOptimisticUpdate = useWorkareaGroupsOptimisticUpdate()
  type MutationProps = {
    jobId: number
    workareaIds: Record<string, number[]>
    optimisticUpdate: WorkareaGroupWithWorkareas[]
  }
  return useMutation({
    mutationFn: ({ jobId, workareaIds }: MutationProps) => sortWorkareas(jobId, workareaIds),
    onMutate: ({ jobId, optimisticUpdate }) => workareaGroupsOptimisticUpdate.setFullOptimisticUpdate(jobId, optimisticUpdate),
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'An error occurred while sorting the workareas'
      notification.error({ message: errorMessage })
    }
  })
}

export const useMoveWorkareaMutation = () => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: (request: MoveWorkareaRequest) => moveWorkarea(request),
    onSuccess: (_, parameters) => {
      workareaGroupsInvalidator.invalidateForJob(parameters.jobId)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'An error occurred while moving the workarea'
      notification.error({ message: errorMessage })
    }
  })
}

export const useMoveWorkareaToHiddenGroupMutation = () => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: (request: MoveWorkareaToHiddenGroupRequest) => moveWorkareaToHiddenGroup(request),
    onSuccess: (_, parameters) => {
      workareaGroupsInvalidator.invalidateForJob(parameters.jobId)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'An error occurred while moving the workarea to hidden group'
      notification.error({ message: errorMessage })
    }
  })
}

export const usePartialEditWorkareaGroupMutation = () => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()
  const workareaGroupsOptimisticUpdate = useWorkareaGroupsOptimisticUpdate()
  const jobItemsInvalidator = useJobItemsInvalidator()

  return useMutation({
    mutationFn: ({ dataForServer }: WorkareaGroupMutationData) => partiallyEditWorkareaGroup(dataForServer),
    onMutate: ({ dataForServer: { jobId }, optimisticUpdate }) => workareaGroupsOptimisticUpdate.setGroupOptimisticUpdate(jobId, optimisticUpdate),
    onSuccess: (_, { dataForServer: { jobId } }) => {
      workareaGroupsInvalidator.invalidateForJob(jobId)
      jobItemsInvalidator.invalidateForJob(jobId)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'An error occurred while editing the workarea group'
      notification.error({ message: errorMessage })
    }
  })
}

export const useCreateWorkareaGroupMutation = (onSuccess: (groupId: string) => void) => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()

  return useMutation({
    mutationFn: createWorkareaGroup,
    onSuccess: (groupId, parameters) => {
      workareaGroupsInvalidator.invalidateForJob(parameters.jobId)
      onSuccess(groupId)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'An error occurred while creating the workarea group'
      notification.error({ message: errorMessage })
    }
  })
}

const workareaAttachmentsKey = (jobId: number) => ['workareaAttachments', { jobId }]

export const useWorkareaAttachmentsQuery = (jobId: number) => {
  return useQuery(workareaAttachmentsKey(jobId), {
    queryFn: () => getWorkareaAttachments(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false,
  })
}

export const useWorkareaAttachmentsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: (jobId: number) => queryClient.invalidateQueries(workareaAttachmentsKey(jobId))
  }
}

export type WorkareaAttachmentTarget = { groupId?: string, workareaId?: number }

export const isForTarget = (row: WorkareaAttachmentDetail, target: WorkareaAttachmentTarget) =>
  target.groupId ? row.workareaGroupId === target.groupId : row.projectWorkareaId === target.workareaId

/**
 * Attachments on the Proposal Description card. A grouped work area stores them on its group,
 * because the group description is what the customer sees; an ungrouped one stores its own.
 *
 * `add` exists because the uploader accepts several files at once and reports each one
 * separately as it finishes. Appending to a snapshot taken at render would let concurrent
 * uploads overwrite each other, so it appends to the cache, which every caller shares.
 *
 * Both write the cache before the request so the next concurrent call sees them, and
 * invalidate on settle rather than success so a rejected write does not leave the optimistic
 * row on screen.
 */
export const useSetWorkareaAttachmentsMutation = (jobId: number) => {
  const queryClient = useQueryClient()
  const invalidator = useWorkareaAttachmentsInvalidator()
  const mutation = useMutation({
    mutationFn: (target: WorkareaAttachmentTarget & { attachmentIds: string[] }) => {
      if (target.groupId) {
        return setGroupAttachments(jobId, target.groupId, target.attachmentIds)
      }
      return setWorkareaAttachments(jobId, target.workareaId!, target.attachmentIds)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.errorMessage || 'Could not save the attachments'
      notification.error({ message: errorMessage })
    },
    onSettled: () => invalidator.invalidate(jobId)
  })

  const readCache = () =>
    queryClient.getQueryData<WorkareaAttachmentDetail[]>(workareaAttachmentsKey(jobId)) ?? []

  return {
    ...mutation,
    replace: (target: WorkareaAttachmentTarget, rows: WorkareaAttachmentDetail[]) => {
      queryClient.setQueryData(workareaAttachmentsKey(jobId),
        [...readCache().filter(x => !isForTarget(x, target)), ...rows])
      mutation.mutate({ ...target, attachmentIds: rows.map(x => x.attachmentId) })
    },
    add: (target: WorkareaAttachmentTarget, row: WorkareaAttachmentDetail) => {
      const next = [...readCache(), row]
      queryClient.setQueryData(workareaAttachmentsKey(jobId), next)
      mutation.mutate({
        ...target,
        attachmentIds: next.filter(x => isForTarget(x, target)).map(x => x.attachmentId)
      })
    }
  }
}
